import { createContext, useContext, useId, useLayoutEffect } from 'react'

/**
 * Which requirement a form marks on its fields. `auto` (the default) marks
 * the less common kind, and nothing when every field is the same; the others
 * force it: `required` or `optional` marks that kind, `both` marks every
 * field, `none` marks nothing.
 */
export type RequirementIndicators = 'auto' | 'required' | 'optional' | 'both' | 'none'

/** A field's own say: `show` marks it whatever its form decides, `hide` never. */
export type RequirementIndicatorOverride = 'auto' | 'show' | 'hide'

export type RequirementMark = 'required' | 'optional' | null

export interface RequirementMarks {
  required: boolean
  optional: boolean
}

export const REQUIREMENT_MARK_LABEL = { required: 'Required', optional: 'Optional' } as const

/**
 * Which kind of field a form marks, from how many of each it has. With `auto`:
 * none when every field is required or every one is optional; otherwise the
 * less common kind ("Optional" on the few optional fields of a mostly required
 * form, "Required" on the few required ones of a mostly optional form), and on
 * a tie the required ones, since those are what stop a save.
 */
export function requirementMarks(
  counts: { required: number; optional: number },
  indicators: RequirementIndicators
): RequirementMarks {
  switch (indicators) {
    case 'none':
      return { required: false, optional: false }
    case 'required':
      return { required: true, optional: false }
    case 'optional':
      return { required: false, optional: true }
    case 'both':
      return { required: true, optional: true }
    case 'auto':
      if (counts.required === 0 || counts.optional === 0) {
        return { required: false, optional: false }
      }
      return counts.optional < counts.required
        ? { required: false, optional: true }
        : { required: true, optional: false }
  }
}

interface FormRequirementsScope {
  register: (id: string, required: boolean) => void
  unregister: (id: string) => void
  marks: RequirementMarks
}

/** A form's fields and the marks it decides; provided by FormRequirements. */
export const FormRequirementsContext = createContext<FormRequirementsScope | null>(null)

/**
 * A field's requirement mark in its form (FormRequirements): it counts the
 * field while `counted` (an editable field a person fills in) and returns the
 * mark the field shows. A field outside any form stands alone, so it shows none
 * unless `override` is `show`. Every labelled control in a form uses this, by
 * way of FieldInput or FieldRow or directly, so the form counts all of them.
 */
export function useRequirementMark({
  required,
  counted = true,
  override = 'auto',
}: {
  required: boolean
  counted?: boolean
  override?: RequirementIndicatorOverride
}): RequirementMark {
  const scope = useContext(FormRequirementsContext)
  const register = scope?.register
  const unregister = scope?.unregister
  const id = useId()

  // Counted before the browser paints, so the marks never flash in or out.
  useLayoutEffect(() => {
    if (!register || !unregister || !counted) return
    register(id, required)
    return () => unregister(id)
  }, [counted, id, register, required, unregister])

  if (override === 'hide') return null
  if (override === 'show') return required ? 'required' : 'optional'
  if (!scope || !counted) return null
  if (required) return scope.marks.required ? 'required' : null
  return scope.marks.optional ? 'optional' : null
}
